import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { Link, Progress } from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  scaffolderApiRef,
  type ScaffolderTask,
} from '@backstage/plugin-scaffolder-react';
import { updatedLabel } from '../home/updatedLabel';
import { repoLocationOf } from './templates';

/**
 * The signed-in colleague's own latest runs (DX-650), each with what it was
 * about, how it ended and a link to its log — so a run whose panel was closed
 * is still one click away. The whole history stays on the engine's own list.
 */

const SHOWN = 5;

const useStyles = makeStyles(theme => ({
  head: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 2),
    marginBottom: theme.spacing(1.5),
  },
  heading: { fontSize: '1rem', fontWeight: 600, margin: 0 },
  aside: { fontSize: '0.8125rem' },
  empty: {
    margin: 0,
    padding: theme.spacing(2),
    borderRadius: 8,
    border: '1px dashed var(--color-brand-200)',
    background: 'var(--ui-bg-elevated)',
    fontSize: '0.875rem',
    color: 'var(--ui-text-muted)',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: theme.spacing(0.5, 0),
    borderRadius: 8,
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
  },
  run: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1, 1.5),
    padding: theme.spacing(1.25, 2),
  },
  mark: {
    flex: 'none',
    display: 'grid',
    placeItems: 'center',
    width: 22,
    height: 22,
    boxSizing: 'border-box',
    borderRadius: '50%',
    color: '#ffffff',
    '& svg': { fontSize: 14 },
  },
  done: { background: 'var(--ui-primary)' },
  failed: { background: 'var(--ui-danger-border)' },
  other: { border: '1px dashed var(--color-brand-300)' },
  what: {
    flex: '1 1 12rem',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  subject: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.875rem',
    fontWeight: 600,
    overflowWrap: 'anywhere',
  },
  detail: { fontSize: '0.8125rem', color: 'var(--ui-text-muted)' },
  state: { fontSize: '0.8125rem', color: 'var(--ui-text-muted)' },
  stateFailed: { color: 'var(--ui-danger-text)' },
}));

const STATES: Record<string, string> = {
  completed: 'Created',
  failed: 'Failed',
  cancelled: 'Cancelled',
  open: 'Waiting',
  processing: 'Running',
};

/** What a run was about: the repository it was given, else its `name`. */
const subjectOf = (task: ScaffolderTask): string => {
  const values = (task.spec.parameters ?? {}) as Record<string, unknown>;
  if (typeof values.repoUrl === 'string') {
    const { owner, repo } = repoLocationOf(values.repoUrl);
    if (owner && repo) return `${owner}/${repo}`;
  }
  if (typeof values.name === 'string' && values.name) return values.name;
  return task.id;
};

const templateOf = (task: ScaffolderTask): string => {
  const entity = task.spec.templateInfo?.entity;
  return (
    entity?.metadata?.title ??
    entity?.metadata?.name ??
    task.spec.templateInfo?.entityRef ??
    'Template'
  );
};

type Props = {
  /** Changed by the page whenever a run it started has finished. */
  refreshKey: number;
};

export const YourRuns = ({ refreshKey }: Props) => {
  const classes = useStyles();
  const scaffolderApi = useApi(scaffolderApiRef);
  const taskLink = useRouteRef(scaffolderPlugin.routes.ongoingTask);
  const allLink = useRouteRef(scaffolderPlugin.routes.listTasks);
  const [runs, setRuns] = useState<{
    loading: boolean;
    failed: boolean;
    tasks: ScaffolderTask[];
  }>({ loading: true, failed: false, tasks: [] });

  useEffect(() => {
    let live = true;
    // The engine answers newest first, so only the runs shown are asked for.
    scaffolderApi.listTasks({ filterByOwnership: 'owned', limit: SHOWN }).then(
      ({ tasks }) => {
        if (!live) return;
        const latest = [...tasks]
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
          .slice(0, SHOWN);
        setRuns({ loading: false, failed: false, tasks: latest });
      },
      () => {
        if (live) setRuns({ loading: false, failed: true, tasks: [] });
      },
    );
    return () => {
      live = false;
    };
  }, [scaffolderApi, refreshKey]);

  let body;
  if (runs.loading) {
    body = <Progress />;
  } else if (runs.failed) {
    body = <p className={classes.empty}>Your runs could not be loaded.</p>;
  } else if (runs.tasks.length === 0) {
    body = (
      <p className={classes.empty}>
        Nothing started yet. A run you start shows here, with each step and its
        log.
      </p>
    );
  } else {
    body = (
      <ul className={classes.list}>
        {runs.tasks.map(task => {
          let mark = (
            <span className={`${classes.mark} ${classes.other}`} aria-hidden />
          );
          if (task.status === 'completed') {
            mark = (
              <span className={`${classes.mark} ${classes.done}`} aria-hidden>
                <CheckIcon />
              </span>
            );
          } else if (task.status === 'failed') {
            mark = (
              <span className={`${classes.mark} ${classes.failed}`} aria-hidden>
                <CloseIcon />
              </span>
            );
          }
          return (
            <li key={task.id} className={classes.run}>
              {mark}
              <span className={classes.what}>
                <span className={classes.subject}>{subjectOf(task)}</span>
                <span className={classes.detail}>{templateOf(task)}</span>
              </span>
              <span
                className={
                  task.status === 'failed'
                    ? `${classes.state} ${classes.stateFailed}`
                    : classes.state
                }
              >
                {STATES[task.status] ?? task.status} ·{' '}
                {updatedLabel(task.createdAt)}
              </span>
              <Link to={taskLink({ taskId: task.id })}>Log</Link>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <section aria-labelledby="create-runs-heading">
      <div className={classes.head}>
        <h2 id="create-runs-heading" className={classes.heading}>
          Your runs
        </h2>
        <Link to={allLink()} className={classes.aside}>
          All runs
        </Link>
      </div>
      {body}
    </section>
  );
};
